import React from 'react';

import {
  endsWithSequence,
  getChordShortcutIfMatch,
  getComboShortcutIfMatch,
  getSequenceShortcutIfMatch,
  isPrefixOfRegisteredSequence,
  shouldIgnoreKeyboardShortcut,
  stringToKeyboardShortcut,
} from '@neptune/shared/keyboard-business-logic';
import {
  ChordShortcut,
  ComboShortcut,
  KeyboardShortcutContext,
  RegisterOpenModalOptions,
  RegisterShortcutOptions,
  SequenceShortcut,
} from '@neptune/shared/keyboard-domain';

const SEQUENCE_LENGTH_LIMIT = 5;
const COMBO_LENGTH_LIMIT = 5;
const RESET_INTERVAL = 1_000;

const stripShortcut = (input: string) =>
  input === ' ' ? input : input.replace(/\s+/g, '').toLowerCase();

type SequencePrefixChord = {
  chord: string;
  registration: symbol;
  keyPressIndex: number;
  undo?: () => void;
};

export const KeyboardShortcutsProvider = ({ children }: { children: React.ReactNode }) => {
  const registeredShortcuts = React.useRef<
    Map<string, (event: KeyboardEvent) => void | (() => void)>
  >(new Map());
  // Identifies each registration, so that state tied to it can be dropped when it unregisters.
  const shortcutRegistrations = React.useRef(new Map<string, symbol>());
  const disableOnModalOpenShortcuts = React.useRef(new Set<string>());
  const allowInFullscreenShortcuts = React.useRef(new Set<string>());
  const registeredChords = React.useRef<Map<string, ChordShortcut>>(new Map());
  const registeredSequences = React.useRef<Map<string, SequenceShortcut>>(new Map());
  const registeredCombos = React.useRef<Map<string, ComboShortcut>>(new Map());
  const timeoutId = React.useRef<number | null>(null);

  // For simultaneously pressed keys, like Alt+Ctrl+Del
  const currentChord = React.useRef<Set<string>>(new Set());
  // For sequentially pressed keys, like G then P
  const currentSequence = React.useRef<string[]>([]);
  // For combination of both, like Alt+P then A
  const currentCombo = React.useRef<Set<string>[]>([new Set()]);
  const keyPressCount = React.useRef(0);
  // A chord that is also the first key of a registered sequence (like c and c > s) fires
  // immediately; its undo runs if that sequence then completes from the same key press.
  const sequencePrefixChord = React.useRef<SequencePrefixChord | null>(null);

  const openModalsRef = React.useRef(new Set<string>());
  const openFullscreensRef = React.useRef(new Set<string>());

  const registerOpenModal = React.useCallback(
    (id: string, { fullscreen }: RegisterOpenModalOptions = {}) => {
      const openEntries = fullscreen ? openFullscreensRef.current : openModalsRef.current;

      openEntries.add(id);

      return () => {
        openEntries.delete(id);
      };
    },
    [],
  );

  const isBlockedByOpenModal = React.useCallback((shortcut: string) => {
    if (!disableOnModalOpenShortcuts.current.has(shortcut)) {
      return false;
    }

    if (openModalsRef.current.size > 0) {
      return true;
    }

    return openFullscreensRef.current.size > 0 && !allowInFullscreenShortcuts.current.has(shortcut);
  }, []);

  const registerShortcut = React.useCallback(
    (
      shortcut: string,
      callback: (event: KeyboardEvent) => void | (() => void),
      { disableOnModalOpen, allowInFullscreen }: RegisterShortcutOptions = {},
    ) => {
      const strippedShortcut = stripShortcut(shortcut);

      if (registeredShortcuts.current.has(strippedShortcut)) {
        // TODO(FRO): restore strict conflict throwing after report DSL preview no longer mounts
        // a second report runtime.
        // throw new Error(`Conflicting shortcut: ${shortcut}`);
        return () => undefined;
      }

      const registration = Symbol(strippedShortcut);

      registeredShortcuts.current.set(strippedShortcut, callback);
      shortcutRegistrations.current.set(strippedShortcut, registration);

      if (disableOnModalOpen) {
        disableOnModalOpenShortcuts.current.add(strippedShortcut);
      }

      if (allowInFullscreen) {
        allowInFullscreenShortcuts.current.add(strippedShortcut);
      }

      const keyboardShortcut = stringToKeyboardShortcut(strippedShortcut);

      switch (keyboardShortcut.type) {
        case 'chord':
          registeredChords.current.set(strippedShortcut, keyboardShortcut);
          break;
        case 'sequence':
          registeredSequences.current.set(strippedShortcut, keyboardShortcut);

          break;
        case 'combo':
          registeredCombos.current.set(strippedShortcut, keyboardShortcut);
          break;
      }

      return () => {
        registeredShortcuts.current.delete(strippedShortcut);
        shortcutRegistrations.current.delete(strippedShortcut);
        disableOnModalOpenShortcuts.current.delete(strippedShortcut);
        allowInFullscreenShortcuts.current.delete(strippedShortcut);
        registeredChords.current.delete(strippedShortcut);
        registeredSequences.current.delete(strippedShortcut);
        registeredCombos.current.delete(strippedShortcut);

        const prefixChord = sequencePrefixChord.current;

        if (prefixChord?.registration === registration) {
          // Its undo is obsolete, and the key press that fired it must not complete a sequence.
          // The buffer ends at the latest key press, so keeping only the key presses after the
          // prefix chord keeps other sequences in progress.
          const keyPressesAfterPrefixChord = keyPressCount.current - prefixChord.keyPressIndex;

          sequencePrefixChord.current = null;
          currentSequence.current =
            keyPressesAfterPrefixChord > 0
              ? currentSequence.current.slice(-keyPressesAfterPrefixChord)
              : [];
        }
      };
    },
    [],
  );

  const clearBuffers = React.useCallback(() => {
    currentChord.current.clear();
    currentSequence.current = [];
    currentCombo.current = [new Set<string>()];
    sequencePrefixChord.current = null;
  }, []);

  const keyDownHandler = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.repeat) {
        return;
      }

      if (shouldIgnoreKeyboardShortcut(event)) {
        return;
      }

      const key = event.key.toLowerCase();

      keyPressCount.current += 1;
      const keyPressIndex = keyPressCount.current;

      currentChord.current.add(key);
      currentSequence.current.push(key);
      currentCombo.current[currentCombo.current.length - 1].add(key);

      if (currentSequence.current.length > SEQUENCE_LENGTH_LIMIT) {
        currentSequence.current.shift();
      }

      const matchedChord = getChordShortcutIfMatch(registeredChords.current, currentChord.current);
      let firedSequencePrefixChord = false;

      if (matchedChord && !isBlockedByOpenModal(matchedChord)) {
        const registration = shortcutRegistrations.current.get(matchedChord);
        const undo = registeredShortcuts.current.get(matchedChord)?.(event);

        if (
          registration &&
          shortcutRegistrations.current.get(matchedChord) === registration &&
          isPrefixOfRegisteredSequence(matchedChord, registeredSequences.current)
        ) {
          currentChord.current.clear();
          currentCombo.current = [new Set<string>()];
          sequencePrefixChord.current = {
            chord: matchedChord,
            registration,
            keyPressIndex,
            undo: typeof undo === 'function' ? undo : undefined,
          };
          firedSequencePrefixChord = true;
        } else {
          clearBuffers();
        }
      }

      const matchedSequence = firedSequencePrefixChord
        ? null
        : getSequenceShortcutIfMatch(registeredSequences.current, currentSequence.current);

      if (matchedSequence && !isBlockedByOpenModal(matchedSequence)) {
        const prefixChord = sequencePrefixChord.current;
        const sequence = registeredSequences.current.get(matchedSequence)?.shortcut ?? [];

        if (
          prefixChord &&
          shortcutRegistrations.current.get(prefixChord.chord) === prefixChord.registration &&
          sequence[0] === prefixChord.chord &&
          endsWithSequence(currentSequence.current, sequence) &&
          keyPressIndex - sequence.length + 1 === prefixChord.keyPressIndex
        ) {
          prefixChord.undo?.();
        }

        registeredShortcuts.current.get(matchedSequence)?.(event);
        clearBuffers();
      }

      const matchedCombo = getComboShortcutIfMatch(registeredCombos.current, currentCombo.current);

      if (matchedCombo && !isBlockedByOpenModal(matchedCombo)) {
        registeredShortcuts.current.get(matchedCombo)?.(event);
        clearBuffers();
      }

      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }

      timeoutId.current = window.setTimeout(clearBuffers, RESET_INTERVAL);
    },
    [clearBuffers, isBlockedByOpenModal],
  );

  const keyUpHandler = React.useCallback((event: KeyboardEvent) => {
    if (shouldIgnoreKeyboardShortcut(event)) {
      return;
    }

    const key = event.key.toLowerCase();

    currentChord.current.delete(key);

    if (currentChord.current.size === 0) {
      // All keys are up, propagate combo
      currentCombo.current.push(new Set());

      if (currentCombo.current.length > COMBO_LENGTH_LIMIT) {
        currentCombo.current.shift();
      }
    }
  }, []);

  React.useEffect(() => {
    window.addEventListener('keydown', keyDownHandler);
    window.addEventListener('keyup', keyUpHandler);

    return () => {
      window.removeEventListener('keydown', keyDownHandler);
      window.removeEventListener('keyup', keyUpHandler);
    };
  }, [keyDownHandler, keyUpHandler]);

  return (
    <KeyboardShortcutContext.Provider value={{ registerShortcut, registerOpenModal }}>
      {children}
    </KeyboardShortcutContext.Provider>
  );
};
